import { useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { Panel, btn, field } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { EdiTag } from "@/components/EdiTag";

const on = (v) => v === true || String(v).toLowerCase() === "true";

export const TestModePanel = ({ cfg, setCfg }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      setRes((await api.post("/test/run")).data);
      toast.success("End-to-end test complete");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const clear = async () => {
    if (!window.confirm("Delete all orders, call-offs, EDI messages and stock moves created in test mode?")) return;
    const r = (await api.delete("/test/data")).data;
    setRes(null);
    toast.success(`Test data cleared: ${r.orders} order(s), ${r.edi_messages} message(s), ${r.inventory_moves_reversed} stock move(s) reversed`);
  };
  return (
    <Panel title="Automation & test mode" testId="test-mode-panel" action={on(cfg.test_mode) && <StatusBadge status="test_hold" label="Test mode on" testId="test-mode-badge" />}>
      <div className="space-y-5 p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex items-start gap-3 text-sm text-slate-200">
            <Switch checked={on(cfg.test_mode)} onCheckedChange={(v) => setCfg({ ...cfg, test_mode: v })} data-testid="settings-test-mode" />
            <span>Test mode<span className="block text-xs text-slate-500">Nothing is transmitted to Group O. Outputs are held for review and ISA15 = T.</span></span>
          </label>
          <label className="flex items-start gap-3 text-sm text-slate-200">
            <Switch checked={on(cfg.auto_release)} onCheckedChange={(v) => setCfg({ ...cfg, auto_release: v })} data-testid="settings-auto-release" />
            <span>Auto-release 940<span className="block text-xs text-slate-500">Amazon orders (SAP export, Coupa or manual entry) generate the 940 straight away when site, items, SCAC and stock are OK.</span></span>
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Default carrier SCAC (W6610)</span>
            <Input value={cfg.default_scac ?? ""} onChange={(e) => setCfg({ ...cfg, default_scac: e.target.value })} placeholder="e.g. AMZF" className={field} data-testid="settings-default-scac" />
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Fallback xDock site</span>
            <Input value={cfg.default_site ?? ""} onChange={(e) => setCfg({ ...cfg, default_site: e.target.value })} className={field} data-testid="settings-default-site" />
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Default freight payment (W6601)</span>
            <Input value={cfg.default_payment ?? ""} onChange={(e) => setCfg({ ...cfg, default_payment: e.target.value.toUpperCase() })} placeholder="PP" className={field} data-testid="settings-default-payment" />
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Default routing (W6605)</span>
            <Input value={cfg.default_routing ?? ""} onChange={(e) => setCfg({ ...cfg, default_routing: e.target.value })} placeholder="3RDPA-TL" className={field} data-testid="settings-default-routing" />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 pt-4">
          <Button onClick={run} disabled={busy || !on(cfg.test_mode)} className={btn.primary} data-testid="test-run-e2e-btn"><FlaskConical className="mr-1.5 h-4 w-4" /> {busy ? "Running…" : "Run end-to-end test"}</Button>
          <Button variant="ghost" onClick={clear} className={btn.danger} data-testid="test-clear-data-btn"><Trash2 className="mr-1.5 h-4 w-4" /> Clear test data</Button>
          <span className="text-xs text-slate-500">Save settings with Test mode on first. The test runs SAP Amazon order → auto 940 → 997 → 945 → 997 using simulated Group O files (test IDs GROUPOSCST).</span>
        </div>
        {res && (
          <div className="rounded-sm border border-slate-800 bg-[#17191F] p-4 text-xs" data-testid="test-run-result">
            <div className="mb-2 text-slate-300">PO <Link to={`/orders/${res.order_id}`} className="font-mono text-sky-300 hover:underline" data-testid="test-run-order-link">{res.po_number}</Link> · final status <StatusBadge status={res.order_status} />{res.hold_reason && <span className="ml-2 text-amber-300">{res.hold_reason}</span>}</div>
            <ol className="space-y-1.5">
              {res.steps.map((s, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2" data-testid={`test-run-step-${i}`}>
                  <span className="font-mono text-slate-500">{i + 1}.</span><EdiTag code={s.doc_type} className="font-mono text-amber-300" /><span className="text-slate-300">{s.label}</span><StatusBadge status={s.status} />{s.error && <span className="text-rose-300">{s.error}</span>}
                </li>
              ))}
            </ol>
            <Link to="/outputs" className="mt-3 inline-block text-sky-300 hover:underline" data-testid="test-run-review-link">Review generated outputs →</Link>
          </div>
        )}
      </div>
    </Panel>
  );
};
